import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { DataTable, StatusBadge, type DataTableColumn } from '@/components/data-table';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/ai-costs')({
  validateSearch: (search: Record<string, unknown>): { page: number } => ({
    page: Math.max(1, Number.parseInt(String(search.page ?? '1'), 10) || 1),
  }),
  component: AiCostsPage,
});

const PAGE_SIZE = 50;

interface CostRow {
  userId: string;
  email: string | null;
  costEUR: number;
  tokensCachedInput: number;
  tokensFreshInput: number;
  tokensOutput: number;
  count: number;
}

const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 4,
});

const numberFormatter = new Intl.NumberFormat('fr-FR');

const COLUMNS: DataTableColumn<CostRow>[] = [
  {
    key: 'account',
    header: 'Compte',
    cell: (row) => <span className="font-medium">{row.email ?? row.userId}</span>,
  },
  {
    key: 'cost',
    header: 'Coût cumulé',
    align: 'right',
    cell: (row) => (
      <span className="font-mono font-semibold tabular-nums">
        {euroFormatter.format(row.costEUR)}
      </span>
    ),
  },
  {
    key: 'cached',
    header: 'Tokens cache',
    align: 'right',
    cell: (row) => (
      <span className="font-mono tabular-nums">
        {numberFormatter.format(row.tokensCachedInput)}
      </span>
    ),
  },
  {
    key: 'fresh',
    header: 'Tokens entrée',
    align: 'right',
    cell: (row) => (
      <span className="font-mono tabular-nums">{numberFormatter.format(row.tokensFreshInput)}</span>
    ),
  },
  {
    key: 'output',
    header: 'Tokens sortie',
    align: 'right',
    cell: (row) => (
      <span className="font-mono tabular-nums">{numberFormatter.format(row.tokensOutput)}</span>
    ),
  },
  {
    key: 'count',
    header: 'Cycles',
    align: 'right',
    cell: (row) => (
      <span className="font-mono tabular-nums">{numberFormatter.format(row.count)}</span>
    ),
  },
];

function AiCostsPage(): React.ReactElement {
  const { page } = Route.useSearch();
  const list = trpc.admin.aiCostList.useQuery({ page, pageSize: PAGE_SIZE });

  const rows = (list.data?.rows ?? []) as CostRow[];
  const total = list.data?.total ?? 0;
  const statusCounts = list.data?.statusCounts ?? {};
  const totalEUR = list.data?.summary.totalEUR ?? 0;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 1;
  const hasNext = page < pageCount;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader
        title="Coûts IA"
        subtitle="Coûts d'IA cumulés par compte (génération CDC, jugement de profil, calendrier)."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="border-border bg-card rounded-2xl border p-4">
          <p className="text-muted-foreground text-sm">Coût IA total</p>
          <p className="mt-2.5 font-mono text-[31px] leading-[0.95] font-bold tracking-[-0.05em] tabular-nums">
            {list.isLoading ? '—' : euroFormatter.format(totalEUR)}
          </p>
        </div>
        {Object.entries(statusCounts).map(([status, count]) => (
          <div key={status} className="border-border bg-card rounded-2xl border p-4">
            <div className="flex items-center justify-between gap-2">
              <StatusBadge
                tone={status === 'SUCCESS' ? 'success' : status === 'ERROR' ? 'danger' : 'neutral'}
                label={status}
              />
            </div>
            <p className="mt-2.5 font-mono text-[31px] leading-[0.95] font-bold tracking-[-0.05em] tabular-nums">
              {numberFormatter.format(count)}
            </p>
          </div>
        ))}
      </div>

      <DataTable
        columns={COLUMNS}
        rows={rows}
        rowKey={(row) => row.userId}
        footer={
          <>
            <span className="text-muted-foreground text-sm">
              {numberFormatter.format(total)} compte{total > 1 ? 's' : ''} — page {page} /{' '}
              {pageCount}
            </span>
            <div className="flex items-center gap-2">
              <Link
                to="/admin/ai-costs"
                search={{ page: Math.max(1, page - 1) }}
                disabled={!hasPrev}
                className="border-border bg-background text-foreground hover:bg-muted rounded-lg border px-3 py-1.5 text-sm font-medium aria-disabled:pointer-events-none aria-disabled:opacity-50"
                aria-disabled={!hasPrev}
              >
                Précédent
              </Link>
              <Link
                to="/admin/ai-costs"
                search={{ page: page + 1 }}
                disabled={!hasNext}
                className="border-border bg-background text-foreground hover:bg-muted rounded-lg border px-3 py-1.5 text-sm font-medium aria-disabled:pointer-events-none aria-disabled:opacity-50"
                aria-disabled={!hasNext}
              >
                Suivant
              </Link>
            </div>
          </>
        }
        emptyState={
          <p className="text-muted-foreground text-center text-sm">
            Aucun coût IA enregistré pour le moment.
          </p>
        }
      />
    </div>
  );
}
